import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/integrations/clients/carAuction/carAuctionClient";
import { toast } from "sonner";
import { commonLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

interface GuestData {
  name: string;
  email: string;
  phone: string | null;
  leadId: string;
}

const MaskData = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [guestData, setGuestData] = useState<GuestData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMasking, setIsMasking] = useState(false);
  const [isMasked, setIsMasked] = useState(false);
  const m = (commonLabels(getLanguage()).maskData ?? {}) as Record<
    string,
    string
  >;

  useEffect(() => {
    if (!token) {
      toast.error(m.invalidLink ?? "Invalid link. Token is missing.");
      navigate("/", { replace: true });
      return;
    }

    const fetchGuestData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get<{ success: boolean; data?: GuestData }>(
          `/guest-data-masking/data?token=${encodeURIComponent(token)}`,
        );

        if (response.success && response.data) {
          setGuestData(response.data);
        } else {
          toast.error(m.invalidOrExpiredLink ?? "Invalid or expired link.");
          navigate("/", { replace: true });
        }
      } catch (error: any) {
        toast.error(
          m.couldNotFetchData ??
            "Could not fetch your data. The link may be invalid or expired.",
        );
        navigate("/", { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    fetchGuestData();
  }, [token, navigate]);

  const handleMaskData = async () => {
    if (!token) return;

    try {
      setIsMasking(true);
      const response = await api.post<{ success: boolean; message?: string }>(
        `/guest-data-masking/mask?token=${encodeURIComponent(token)}`,
      );

      if (response.success) {
        setIsMasked(true);
        toast.success(
          m.dataMaskedSuccess ??
            "Your personal data has been masked in accordance with GDPR.",
        );
      } else {
        toast.error(
          m.maskError ?? "Could not mask your data. Please try again.",
        );
      }
    } catch (error: any) {
      toast.error(m.maskError ?? "Could not mask your data. Please try again.");
    } finally {
      setIsMasking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-lg">
          {m.loading ??
            commonLabels(getLanguage()).misc?.loading ??
            "Loading..."}
        </div>
      </div>
    );
  }

  if (!guestData) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-6 bg-card backdrop-blur">
        <h1 className="text-2xl font-bold mb-4">
          {m.title ?? "Your personal data"}
        </h1>

        {isMasked ? (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-green-800 dark:text-green-200 font-medium">
                ✓{" "}
                {m.dataMaskedSuccess ??
                  "Your personal data has been masked in accordance with GDPR."}
              </p>
            </div>
            <p className="text-muted-foreground">
              {m.dataMaskedDescription ??
                "Your data has now been masked in our system. You will no longer be able to see it here."}
            </p>
            <Button onClick={() => navigate("/")} className="w-full">
              {m.backToHome ?? "Back to home page"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              {m.intro ??
                "Here is the personal data we have stored from your valuation:"}
            </p>

            <div className="space-y-3">
              <div className="p-4 bg-muted rounded-lg">
                <div className="text-sm font-medium text-muted-foreground mb-1">
                  {m.name ?? "Name"}
                </div>
                <div className="text-base">{guestData.name}</div>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <div className="text-sm font-medium text-muted-foreground mb-1">
                  {m.email ?? "Email"}
                </div>
                <div className="text-base">{guestData.email}</div>
              </div>

              {guestData.phone && (
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    {m.phone ?? "Phone"}
                  </div>
                  <div className="text-base">{guestData.phone}</div>
                </div>
              )}
            </div>

            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {m.gdprNotice ??
                  "GDPR right: Under GDPR you have the right to request that we mask your personal data. If you click the button below we will mask your data in our system."}
              </p>
            </div>

            <Button
              onClick={handleMaskData}
              disabled={isMasking}
              variant="destructive"
              className="w-full"
            >
              {isMasking
                ? (m.masking ?? "Masking...")
                : (m.maskMyData ?? "I want to mask my data")}
            </Button>

            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="w-full"
            >
              {m.cancel ?? "Cancel"}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default MaskData;
